import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  OFFER_TYPE_CRUISE,
} from 'constants/offer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import LayoutContainer from 'components/Common/LayoutContainer'
import { buildSearchStringFromFilters } from 'lib/search/searchUtils'

import Heading from 'components/Luxkit/Typography/Heading'
import useOfferList from 'hooks/Offers/useOfferList'
import { take, without } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import CruiseYouMayAlsoLikeTile from 'components/OfferPage/Cruises/YouMayAlsoLike/CruiseYouMayAlsoLikeTile'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import Group from 'components/utils/Group'
import uuidV4 from 'lib/string/uuidV4Utils'
import GeoContext from 'contexts/geoContext'
import { productClick } from 'analytics/enhanced-ecommerce'
import { productClickObject } from 'analytics/eventDefinitions'
import TrackedCardCarousel from '../TrackedCardCarousel'
import TrackingContext from 'contexts/trackingContext'
import { fetchOfferById } from 'actions/OfferActions'
import { rem } from 'polished'
import { offerHasValidDepartures } from 'lib/cruises/cruiseUtils'

const emptyFilters: App.OfferListFilters = EmptyObject

interface Props {
  title?: string;
  id?: string;
  filters?: App.OfferListFilters;
  offerId?: string;
}

export const RECOMMENDATION_TYPES: App.OfferType[] = [
  OFFER_TYPE_CRUISE,
]

function CruiseRecommendations(props: Props) {
  const {
    id,
    filters = emptyFilters,
    offerId,
  } = props

  const [filtersToUse, setFiltersToUse] = useState<App.OfferListFilters>(filters)
  const [offerIdsList, setOfferIdsList] = useState<string[]>([])

  const [offersToDisplay, setOffersToDisplay] = useState<App.CruiseOffer[]>([])
  const dispatch = useAppDispatch()

  const offerList = useOfferList({ ...filtersToUse, offerTypes: RECOMMENDATION_TYPES })

  const listName = 'Recommendations - Cruises'
  const tracking = useMemo(() => ({
    category: 'Recommendations',
    label: listName,
    listName,
    listId: uuidV4(),
  }), [])
  const geo = useContext(GeoContext)

  const offerIds = useMemo(() => {
    const offerCandidates = offerIdsList?.filter(id => id !== ('cruise:' + offerId))
    return take(without(offerCandidates, id ?? ''), 6)
  }, [id, offerIdsList, offerId])

  const allOffers = useAppSelector(state => state.offer.offers)
  const offers = useMemo(():App.CruiseOffer[] => offerIds
    .map<any>(id => allOffers[id])
    .filter((offer) => !!offer && offerHasValidDepartures(offer))
  , [offerIds, allOffers])

  useEffect(() => {
    if (offerIds.length === 0) return
    offerIds.forEach(id => dispatch(fetchOfferById(id)))
  }, [offers, allOffers, offerIds, dispatch])

  useEffect(() => {
    setOffersToDisplay(offers)
  }, [offers, allOffers])

  useEffect(() => {
    setOfferIdsList((currentOfferIds) => [...currentOfferIds, ...offerList.offerIds])
    if (!offerList.fetching && offerList.offerIds.length < 3) setFiltersToUse({ ...filters, cruiseLines: undefined })
  }, [offerList, filters])

  const onClick = useCallback((
    offer: App.CruiseOffer,
    position: number,
  ) => {
    productClick(geo.currentCurrency, productClickObject(offer, position), {
      listId: tracking.listId,
      listName,
    })
  }, [geo.currentCurrency, tracking.listId])

  if (!offersToDisplay.length) {
    return null
  }

  return (
    <TrackingContext.Provider value={tracking}>
      <Pane>
        <PaneBody size="xl-y">
          <LayoutContainer>
            <Group direction="vertical" gap={16}>
              <Heading variant="heading3">You may also like</Heading>
              <TrackedCardCarousel pageSize={3} width={`min(${rem(366)}, 90%)`}>
                {offersToDisplay.map((offer, i) => (
                  <CruiseYouMayAlsoLikeTile
                  position={i + 1}
                  onClick={onClick}
                  key={offer.id}
                  offer={offer}
                  linkQuery={buildSearchStringFromFilters(filters)}/>
                ))}
              </TrackedCardCarousel>
            </Group>
          </LayoutContainer>
        </PaneBody>
      </Pane>
    </TrackingContext.Provider>
  )
}

export default memo(CruiseRecommendations)
